import { Injectable, WritableSignal } from '@angular/core';
import { isDefined, isEqual } from '@fi-shared/utils';
import { IChatConversationMessage } from '@fi-shared/fi-chat/fi-chat';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { FiChatConversationMessageType } from '@fi-shared/fi-chat/fi-chat-conversation-message-type';
import {
    FiScrollToElementAnimateService,
    IScrollToElementWithCompletionArgs,
} from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { ActionType, PersonalNotificationsCategoryType } from '@fi-shared/types';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiChatSourceMessageType } from '../fi-chat-source-message-type';
import { RelativeDateService } from '@fi-shared/relative-date/relative-date.service';
import { IReactionData, IReactionModel } from '@fi-shared/fi-likes/fi-likes';

@Injectable()
export class FiChatConversationHelperService {
    private readonly scrollToMessageAnimationTimeMS: number = 500;

    constructor(
        private fiChatHelperService: FiChatHelperService,
        private scrollToElementAnimateService: FiScrollToElementAnimateService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private fiUserDataService: FiUserDataService,
        private relativeDateService: RelativeDateService,
    ) {}

    getPreviousMessage(
        currentMessageIndex: number,
        messages: IChatConversationMessage[],
    ): IChatConversationMessage | undefined {
        const previousMessage: IChatConversationMessage = messages[currentMessageIndex + 1];
        if (!isDefined(previousMessage)) return undefined;

        if (previousMessage.IsHidden) return this.getPreviousMessage(currentMessageIndex + 1, messages);

        return previousMessage;
    }

    getNextMessage(
        currentMessageIndex: number,
        messages: IChatConversationMessage[],
    ): IChatConversationMessage | undefined {
        const nextMessage: IChatConversationMessage = messages[currentMessageIndex - 1];
        if (!isDefined(nextMessage)) return undefined;

        if (nextMessage.IsHidden) return this.getNextMessage(currentMessageIndex - 1, messages);

        return nextMessage;
    }

    getHoursDifference(message: IChatConversationMessage, messages: IChatConversationMessage[]): number {
        const currentMessageIndex = messages.indexOf(message);
        if (currentMessageIndex !== -1) {
            if (currentMessageIndex === messages.length - 1) {
                return 1; // should show date for the earliest message
            }
            return this.fiChatHelperService.getHoursDifference(
                message.CreatedOn,
                messages[currentMessageIndex + 1].CreatedOn,
            );
        }
        return undefined;
    }

    isCreatedByUserNameShown(message: IChatConversationMessage, messages: IChatConversationMessage[]): boolean {
        const currentMessageIndex: number = messages.findIndex((conversationMessage) => {
            return isEqual(conversationMessage, message);
        });

        if (currentMessageIndex === messages.length - 1) return true;

        if (this.getHoursDifference(message, messages) * 60 > 5) {
            // 5 mins
            return true;
        }

        const previousMessage: IChatConversationMessage = this.getPreviousMessage(currentMessageIndex, messages);
        return !isDefined(previousMessage) || message.CreatedByUserId !== previousMessage.CreatedByUserId;
    }

    isUserProfileImageShown(message: IChatConversationMessage, messages: IChatConversationMessage[]): boolean {
        const currentMessageIndex: number = messages.findIndex((conversationMessage) => {
            return isEqual(conversationMessage, message);
        });

        if (currentMessageIndex === 0) return true;

        const nextMessage: IChatConversationMessage = this.getNextMessage(currentMessageIndex, messages);
        if (isDefined(nextMessage)) {
            return (
                (this.isSystemMessage(nextMessage.Type) &&
                    nextMessage.Type !== FiChatConversationMessageType.Deleted) ||
                this.isCreatedByUserNameShown(nextMessage, messages) ||
                message.CreatedByUserId !== nextMessage.CreatedByUserId
            );
        }

        return true;
    }

    isMessageDateShown(message: IChatConversationMessage, messages: IChatConversationMessage[]): boolean {
        const currentMessageIndex: number = messages.findIndex((conversationMessage) => {
            return isEqual(conversationMessage, message);
        });

        if (currentMessageIndex === messages.length - 1) return true;

        const previousMessage: IChatConversationMessage = this.getPreviousMessage(currentMessageIndex, messages);
        if (!isDefined(previousMessage)) return true;

        const currentMessageDate = new Date(message.CreatedOn);
        const previousMessageDate = new Date(previousMessage.CreatedOn);

        return (
            currentMessageDate.getUTCFullYear() !== previousMessageDate.getUTCFullYear() ||
            currentMessageDate.getUTCMonth() !== previousMessageDate.getUTCMonth() ||
            currentMessageDate.getUTCDate() !== previousMessageDate.getUTCDate()
        );
    }

    getConversationMessageWrapperId(messageId: number): string {
        return `conversation-message-${messageId}`;
    }

    scrollToMessage(
        message: IChatConversationMessage,
        scrollableElement: HTMLElement,
        className: string,
        highlightTime: number,
    ): void {
        const targetElement: HTMLElement = document.getElementById(this.getConversationMessageWrapperId(message.Id));
        const scrollToMessageArgs: IScrollToElementWithCompletionArgs = {
            scrollableElement,
            targetElement,
            maxScrollDurationMS: this.scrollToMessageAnimationTimeMS,
        };
        this.scrollToElementAnimateService.scrollToElementWithCompletion(scrollToMessageArgs).then(() => {
            targetElement.classList.add(className);
            setTimeout(() => targetElement.classList.remove(className), highlightTime);
        });
    }

    getConfirmDeleteMessageDialog(isDeleteForUser: boolean): IFiDialogResult {
        return this.fiDialogsService.confirmSimpleDelete({
            headerText: this.appTextService.get(
                isDeleteForUser ? 'chat.delete_message_for_you' : 'chat.delete_message',
            ),
            messageText: this.appTextService.get(
                isDeleteForUser ? 'chat.delete_message_for_you_confirm' : 'chat.delete_message_confirm',
            ),
        });
    }

    getSourceMessageToScrollTo(
        message: IChatConversationMessage,
        messages: IChatConversationMessage[],
        skipMessage: IChatConversationMessage,
    ): IChatConversationMessage {
        const startIndex: number = messages.findIndex((item) => item.Id === skipMessage.Id);
        if (startIndex === -1) return undefined;

        return messages.slice(startIndex).find((item: IChatConversationMessage) => {
            if (item.ConversationId === message.ConversationId) return item.Id === message.Id;
            return !item.Content?.length && item.SourceMessageId === message.Id && item.Id !== skipMessage.Id;
        });
    }

    shouldMarkConversationAsVisited(
        notificationCategory: PersonalNotificationsCategoryType,
        messages: IChatConversationMessage[],
    ): boolean {
        return (
            notificationCategory < PersonalNotificationsCategoryType.Visited ||
            messages.some((message) => {
                return message.NotificationCategory < PersonalNotificationsCategoryType.Visited;
            })
        );
    }

    // conversation message section
    isOutgoingMessage(messageCreatedByUserId: number): boolean {
        return messageCreatedByUserId === this.fiUserDataService.getUserId();
    }

    isMessageDeleted(messageType: FiChatConversationMessageType): boolean {
        return messageType === FiChatConversationMessageType.Deleted;
    }

    isNonDeletedSystemMessage(messageType: FiChatConversationMessageType): boolean {
        return this.isSystemMessage(messageType) && this.isMessageDeleted(messageType);
    }
    isSystemMessage(messageType: FiChatConversationMessageType): boolean {
        return messageType !== FiChatConversationMessageType.Common;
    }

    // Moved from component
    isSourceMessageReplied(message: IChatConversationMessage): boolean {
        return message.SourceMessageType === FiChatSourceMessageType.Reply;
    }

    isSourceDeleted(sourceMessage: IChatConversationMessage): boolean {
        return this.isMessageDeleted(sourceMessage.Type);
    }

    getSourceMessageHeader(message: IChatConversationMessage, sourceMessage: IChatConversationMessage): string {
        if (!this.isSourceMessageForwarded(message) && !this.isSourceMessageReplied(message)) {
            return '';
        }
        const parts: string[] = [this.sourceMessageTypeLabel(message, sourceMessage)];
        if (this.isSourceEdited(sourceMessage)) {
            parts.push(`<i>(${this.appTextService.get('chat.message_edited')})</i>`);
        }
        parts.push(`&#183; ${this.relativeDateService.getDateForSourceMessage(sourceMessage.CreatedOn)}`);
        return parts.join(' ');
    }

    getSourceMessageContent(message: IChatConversationMessage, sourceMessage: IChatConversationMessage): string {
        if (this.isSourceDeleted(sourceMessage)) {
            return this.isCurrentUserMessageSource(message, sourceMessage)
                ? this.appTextService.get('chat.conversation_system_message_delete_outgoing')
                : this.appTextService.get('chat.conversation_system_message_delete_incoming', {
                      '{0}': this.getCreatedByUserName(sourceMessage),
                  });
        }
        if (this.isOnlyAttachmentsMessage(sourceMessage)) {
            return this.getAttachmentsCountLabel(this.getAttachmentsCount(sourceMessage));
        }
        return sourceMessage.Content;
    }

    isOnlyAttachmentsMessage(message: IChatConversationMessage): boolean {
        return !message.Content?.trim().length && this.hasAttachments(message);
    }

    getAttachmentsCount(message: IChatConversationMessage): number {
        return message.MediaBankFileReferences?.length || message.Links?.length || 0;
    }

    getAttachmentsCountLabel(attachmentsCount: number): string {
        if (attachmentsCount === 1) {
            return this.appTextService.get('chat.sent_single_attachment');
        }
        return this.appTextService.get('chat.sent_multiple_attachments', {
            '{0}': attachmentsCount,
        });
    }

    isSourceMessageForwarded(message: IChatConversationMessage): boolean {
        return message.SourceMessageType === FiChatSourceMessageType.Forwarded;
    }

    isOnlySourceMessage(message: IChatConversationMessage): boolean {
        return this.fiChatHelperService.isOnlySourceMessage(message);
    }

    isNoPadding(message: IChatConversationMessage, isCreatedByUserNameShown: boolean): boolean {
        return !isCreatedByUserNameShown && !message.Content && !this.isMessageEdited(message);
    }

    isIncomingMessage(message: IChatConversationMessage): boolean {
        return (
            !this.isOutgoingMessage(message.CreatedByUserId) &&
            !this.isSystemMessage(message.Type) &&
            isDefined(message.Content)
        );
    }

    isMessageEdited(message: IChatConversationMessage): boolean {
        return this.fiChatHelperService.isMessageEdited(message.CreatedOn, message.ChangedOn);
    }

    hasAttachments(message: IChatConversationMessage): boolean {
        return this.fiChatHelperService.hasAttachments(message);
    }

    isConversationMessageInfoShown(message: IChatConversationMessage, isCreatedByUserNameShown: boolean): boolean {
        return isCreatedByUserNameShown || this.isMessageEdited(message);
    }

    isMessageSenderNameShown(message: IChatConversationMessage, isCreatedByUserNameShown: boolean): boolean {
        return (
            !this.isOutgoingMessage(message.CreatedByUserId) &&
            (!this.isMessageEdited(message) || isCreatedByUserNameShown)
        );
    }

    isMessageReadStatusShown(message: IChatConversationMessage, isLastMessage: boolean): boolean {
        return isLastMessage && !!message.ReadBy.length;
    }

    isCurrentUserMessageSource(message: IChatConversationMessage, sourceMessage: IChatConversationMessage): boolean {
        return (
            (!this.isOutgoingMessage(message.CreatedByUserId) && this.isCurrentUserSourceAuthor(sourceMessage)) ||
            (this.isOutgoingMessage(message.CreatedByUserId) && this.isSameAuthor(message, sourceMessage))
        );
    }

    isCurrentUserSourceAuthor(sourceMessage: IChatConversationMessage): boolean {
        return this.fiChatHelperService.isCurrentUser(sourceMessage.CreatedByUserId);
    }

    isSameAuthor(message: IChatConversationMessage, sourceMessage: IChatConversationMessage): boolean {
        return sourceMessage.CreatedByUserId === message.CreatedByUserId;
    }

    isSourceEdited(sourceMessage: IChatConversationMessage): boolean {
        return this.fiChatHelperService.isMessageEdited(sourceMessage.CreatedOn, sourceMessage.ChangedOn);
    }

    sourceMessageTypeLabel(message: IChatConversationMessage, sourceMessage: IChatConversationMessage): string {
        if (this.isSourceMessageReplied(message)) {
            return this.getReplyMessageHeader(message, sourceMessage);
        }
        if (this.isSourceMessageForwarded(message)) {
            return this.getForwardMessageHeader(message, sourceMessage);
        }
        return '';
    }

    getReplyMessageHeader(message: IChatConversationMessage, sourceMessage: IChatConversationMessage): string {
        if (!this.isOutgoingMessage(message.CreatedByUserId)) {
            if (this.isCurrentUserSourceAuthor(sourceMessage)) {
                return this.appTextService.get('chat.replied_to_you');
            }
            if (this.isSameAuthor(message, sourceMessage)) {
                return this.appTextService.get('chat.replied_to_themselves');
            }
        }
        return this.isSameAuthor(message, sourceMessage)
            ? this.appTextService.get('chat.replied_to_yourself')
            : this.appTextService.get('chat.replied_to_user', {
                  '{0}': this.getCreatedByUserName(sourceMessage),
              });
    }

    getCreatedByUserName(message: IChatConversationMessage): string {
        return this.fiChatHelperService.getCreatedByUserName(message);
    }

    getForwardMessageHeader(message: IChatConversationMessage, sourceMessage: IChatConversationMessage): string {
        if (this.isCurrentUserSourceAuthor(sourceMessage)) {
            return this.appTextService.get(
                this.isOutgoingMessage(message.CreatedByUserId)
                    ? 'chat.forwarded_from_yourself'
                    : 'chat.forwarded_from_you',
            );
        }
        if (this.isSameAuthor(message, sourceMessage)) {
            return this.appTextService.get('chat.forwarded_from_themselves');
        }
        return this.appTextService.get('chat.forwarded_from_user', {
            '{0}': this.getCreatedByUserName(sourceMessage),
        });
    }
}
